import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSessionSocket } from '../lib/useSessionSocket.ts';
import { formatCountdown, useCountdown } from '../lib/useCountdown.ts';
import { useSlideshow } from '../lib/useSlideshow.ts';
import { qrUrl } from '../lib/api.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';
import SlideshowStage from '../components/SlideshowStage.tsx';
import QrPlaceCard from '../components/QrPlaceCard.tsx';
import Ticker from '../components/Ticker.tsx';

export default function Slideshow() {
  const { sessionId = '' } = useParams();
  useDocumentTitle('Slideshow');
  const { status, session, media, messages } = useSessionSocket({ sessionId });
  const settings = session?.settings ?? null;
  const { current, upcoming, slideKey, advance } = useSlideshow(media, settings);

  const visibleMessages = messages.filter((m) => !m.hidden);
  const tickerShowing = (settings?.showTicker ?? true) && visibleMessages.length > 0;

  useWakeLock();
  const idle = useIdleCursor();
  const isDemo = session?.plan === 'demo';
  const remainingMs = useCountdown(isDemo ? session?.expiresAt : null);

  if (status === 'gone') {
    return (
      <Centered>
        <h1 className="font-display text-4xl">This slideshow has ended</h1>
        {isDemo ? (
          <>
            <p className="mt-3 text-muted">
              Demo walls run for a few minutes, then everything on them is deleted.
            </p>
            <Link className="btn btn-primary mt-6" to="/">
              Make one of your own
            </Link>
          </>
        ) : (
          <p className="mt-3 text-muted">The event it belonged to is no longer available.</p>
        )}
      </Centered>
    );
  }

  return (
    <main
      className={`fixed inset-0 overflow-hidden bg-black ${idle ? 'cursor-none' : ''}`}
      aria-live="polite"
    >
      <SlideshowStage
        current={current}
        upcoming={upcoming}
        slideKey={slideKey}
        transition={settings?.transition ?? 'fade'}
        slideDurationMs={settings?.slideDurationMs ?? 8000}
        showCaption={settings?.showCaptions ?? true}
        qrPosition={settings?.qrPosition ?? 'bottom-right'}
        liftAboveTicker={tickerShowing}
        onEnded={advance}
      />

      {tickerShowing ? (
        <Ticker messages={visibleMessages} speed={settings?.tickerSpeed ?? 'normal'} />
      ) : null}

      {session && current ? (
        <QrPlaceCard
          sessionId={session.id}
          position={settings?.qrPosition ?? 'bottom-right'}
          size={settings?.qrSize ?? 'md'}
          prompt={settings?.qrPrompt ?? 'Scan to add your photos!'}
          liftAboveTicker={tickerShowing}
        />
      ) : null}

      {session && !current ? (
        <EmptyStage
          sessionId={session.id}
          prompt={settings?.qrPrompt ?? ''}
          tickerShowing={tickerShowing}
        />
      ) : null}

      {/* Quiet, and out of the QR card's corner: the wall is the point, not the clock. */}
      {remainingMs !== null ? (
        <div
          className={`absolute top-[3vh] z-30 rounded-full bg-black/55 px-[1.2vw] py-[0.5vh] text-[1.1vw] text-chalk/80 backdrop-blur ${
            settings?.qrPosition === 'top-left' ? 'right-[2.5vw]' : 'left-[2.5vw]'
          }`}
        >
          Demo wall · {formatCountdown(remainingMs)} left
        </div>
      ) : null}

      {status !== 'live' ? (
        <div className="absolute top-4 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/70 px-4 py-1.5 text-sm text-chalk/80 backdrop-blur">
          {status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
        </div>
      ) : null}
    </main>
  );
}

/**
 * Nothing uploaded yet: the QR becomes the whole screen instead of a corner card.
 * Sits below the ticker, because messages often arrive before the first photo.
 */
function EmptyStage({
  sessionId,
  prompt,
  tickerShowing,
}: {
  sessionId: string;
  prompt: string;
  tickerShowing: boolean;
}) {
  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-dusk">
      <div
        className={`flex flex-col items-center px-8 text-center ${tickerShowing ? 'pb-[13vh]' : ''}`}
      >
        <div className="place-card rotate-[-1deg] p-[1.6vw]">
          <img src={qrUrl(sessionId, 'lg')} alt="QR code to upload photos" className="w-[26vw]" />
        </div>
        <h1 className="font-display mt-[3vh] text-[3.4vw] leading-tight font-semibold">
          {prompt || 'Scan to add your photos!'}
        </h1>
        <p className="mt-[1.2vh] text-[1.6vw] text-muted">
          The first photo takes the screen the moment it lands.
        </p>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="fixed inset-0 grid place-items-center bg-dusk text-center">
      <div>{children}</div>
    </main>
  );
}

/** Keeps the TV awake for the length of the party. */
function useWakeLock(): void {
  const lockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    let cancelled = false;
    const request = async () => {
      try {
        if (!('wakeLock' in navigator)) return;
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          await lock.release();
          return;
        }
        lockRef.current = lock;
      } catch {
        // Not fatal: some browsers refuse without a user gesture.
      }
    };
    void request();

    // The lock is dropped whenever the tab is backgrounded, so retake it.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void request();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void lockRef.current?.release().catch(() => {});
      lockRef.current = null;
    };
  }, []);
}

function useIdleCursor(delayMs = 3000): boolean {
  const [idle, setIdle] = useState(true);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onMove = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), delayMs);
    };
    window.addEventListener('mousemove', onMove);
    timer = setTimeout(() => setIdle(true), delayMs);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(timer);
    };
  }, [delayMs]);

  return idle;
}
